import axios from 'axios';
import store from '@/store';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import Vue from 'vue';
import { cloneDeep } from 'lodash';
import ErrorResponse from '@/ts/ErrorResponse';
import { Mutations, Endpoints } from '@/ts/store/Constants';
import { TableView, TableViewConfig, TableConfig } from '@/ts/interfaces/TableConfigTypes';

@Module({name: 'TableConfigs', namespaced: true})
export default class TableConfigs extends VuexModule {
    public tableConfigs: {[index: string]: TableConfig } = {};

    @Mutation
    public RESET_TABLE_CONFIG_STORE_STATE() {
        this.tableConfigs = {};
    }

    get getTableConfig() {
        return (key: string) => {
            return this.tableConfigs[key];
        };
    }

    @Mutation
    public SET_TABLE_CONFIG(payload: {key: string, config: TableConfig}) {
        Vue.set(this.tableConfigs, payload.key, payload.config);
    }

    @Mutation
    public SET_SELECTED_VIEW(payload: {key: string, view: TableView}) {
        const tableConfig: TableConfig = this.tableConfigs[payload.key];
        if (!tableConfig) {
            return;
        }
        // Making copy of view to eliminate change of reference to view in optionlist
        const viewCopy = cloneDeep(payload.view);
        tableConfig.selectedView = viewCopy;
    }

    @Mutation
    public SET_SELECTED_VIEW_CONFIG(payload: {key: string, config: TableViewConfig}) {
        const tableConfig: TableConfig = this.tableConfigs[payload.key];
        if (!tableConfig) {
            return;
        }
        Vue.set(tableConfig.selectedView, 'config', payload.config);
    }

    @Mutation
    public SET_VIEW_OPTION_LIST(payload: {key: string, viewOptionList: TableView[]}) {
        const tableConfig: TableConfig = this.tableConfigs[payload.key];
        if (!tableConfig) {
            return;
        }
        tableConfig.viewOptionList = payload.viewOptionList;
    }

    @Mutation
    public UPDATE_VIEW_OPTION(payload: {key: string, view: TableView}) {
        const tableConfig: TableConfig = this.tableConfigs[payload.key];
        if (!tableConfig) {
            return;
        }

        const tableOptionIndex = tableConfig.viewOptionList.findIndex((view) => view.id === payload.view.id);
        if (tableOptionIndex !== -1) {
            Vue.set(this.tableConfigs[payload.key].viewOptionList, tableOptionIndex, payload.view);
        }
    }

    @Mutation
    public REMOVE_VIEW_OPTION(payload: {key: string, view: TableView}) {
        const tableConfig: TableConfig = this.tableConfigs[payload.key];
        if (!tableConfig) {
            return;
        }

        const viewIndex = tableConfig.viewOptionList.findIndex((view) => view.id === payload.view.id);
        if (viewIndex !== -1) {
            this.tableConfigs[payload.key].viewOptionList.splice(viewIndex, 1);
        }
    }

    @Mutation
    public ADD_VIEW_OPTION(payload: {key: string, view: TableView}) {
        const tableConfig: TableConfig = this.tableConfigs[payload.key];
        if (!tableConfig) {
            return;
        }
        tableConfig.viewOptionList.push(payload.view);
    }

    @Mutation
    public SET_TABLE_VIEW_DEFAULT_VALUE(payload: {key: string, view: TableView, value: boolean}) {
        const tableConfig: TableConfig = this.tableConfigs[payload.key];
        if (!tableConfig) {
            return;
        }

        if (payload.view.id === tableConfig.selectedView.id) {
            tableConfig.selectedView.default = payload.value;
        }

        const tableOptionIndex = tableConfig.viewOptionList.findIndex((view) => view.id === payload.view.id);
        if (tableOptionIndex !== -1) {
            const tableOption = tableConfig.viewOptionList[tableOptionIndex];
            tableOption.default = payload.value;
            Vue.set(tableConfig.viewOptionList, tableOptionIndex, tableOption);
        }
    }

    @Action
    public DELETE_TABLE_VIEW(payload: {key: string, view: TableView}) {
        const tableConfig = store.getters['TableConfigs/getTableConfig'](payload.key);
        if (!tableConfig) {
            return;
        }

        return axios.delete(Endpoints.Admin.Profile.ADMIN_DELETE_TABLE_VIEW(payload.view.id))
            .then(() => {
                // Removes option from list
                store.commit(Mutations.REMOVE_VIEW_OPTION, payload);

                // Checks if any other default option exists, else uses table default if option is also the selected option
                if (payload.view.id === tableConfig.selectedView.id) {
                    const defaultView = tableConfig.viewOptionList.find((view) => view.default) || tableConfig.defaultView;
                    store.commit(Mutations.SET_SELECTED_VIEW, {
                        key: payload.key,
                        view: defaultView,
                    });
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public UPDATE_TABLE_VIEW_DEFAULT_VALUE(payload: {key: string, view: TableView, value: boolean}) {
        const tableConfig: TableConfig = store.getters['TableConfigs/getTableConfig'](payload.key);
        if (!tableConfig) {
            return;
        }

        return axios.put(Endpoints.Admin.Profile.ADMIN_SAVE_TABLE_VIEW(payload.view.id), {default: payload.value})
            .then(() => {
                // If true that means existing default values in optionlist must be reset to false
                if (payload.value) {
                    const existingDefaultView = tableConfig.viewOptionList.find((view) => view.default);
                    if (existingDefaultView) {
                        store.commit(Mutations.SET_TABLE_VIEW_DEFAULT_VALUE, {
                            key: payload.key,
                            view: existingDefaultView,
                            value: false,
                        });
                    }
                }

                store.commit(Mutations.SET_TABLE_VIEW_DEFAULT_VALUE, {
                    key: payload.key,
                    view: payload.view,
                    value: payload.value,
                });
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public CREATE_NEW_VIEW(payload: {key: string, name: string}) {
        const tableConfig: TableConfig = store.getters['TableConfigs/getTableConfig'](payload.key);
        if (!tableConfig) {
            return;
        }
        const defaultTableViewConfig: TableViewConfig = cloneDeep(tableConfig.defaultView.config);

        const newTableView: TableView = {
            name: payload.name,
            entity: payload.key,
            default: false,
            config: defaultTableViewConfig,
        };

        return axios.post(Endpoints.Admin.Profile.ADMIN_CREATE_TABLE_VIEW(), newTableView)
            .then((response) => {
                store.commit(Mutations.ADD_VIEW_OPTION, {
                    key: response.data.entity,
                    view: response.data,
                });

                store.commit(Mutations.SET_SELECTED_VIEW, {
                    key: response.data.entity,
                    view: response.data,
                });
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public SAVE_SELECTED_TABLE_VIEW(payload: {key: string}) {
        const tableConfig: TableConfig = store.getters['TableConfigs/getTableConfig'](payload.key);
        if (!tableConfig) {
            return;
        }

        return axios.put(Endpoints.Admin.Profile.ADMIN_SAVE_TABLE_VIEW(tableConfig.selectedView.id), {config: tableConfig.selectedView.config})
            .then((response) => {
                const savedView = response.data;
                store.commit(Mutations.UPDATE_VIEW_OPTION, {key: payload.key, view: savedView});
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public INITIALIZE_TABLE_CONFIG(payload: {key: string, defaultTableConfig: TableViewConfig, enableViews: boolean}) {
        if (typeof store.getters['TableConfigs/getTableConfig'](payload.key) === 'undefined') {
            const defaultViewFromProps: TableView = {
                name: 'default',
                default: true,
                entity: payload.key,
                config: payload.defaultTableConfig,
            };

            const tableConfig: TableConfig = {
               defaultView: cloneDeep(defaultViewFromProps),
               selectedView: cloneDeep(defaultViewFromProps),
               viewOptionList:  [],
            };

            store.commit(Mutations.SET_TABLE_CONFIG, {key: payload.key, config: tableConfig});

            // Fetches optionList from api if views functionality are enabled
            if (payload.enableViews) {
                const params = new URLSearchParams();
                params.append('entity', payload.key);

                axios.get(Endpoints.Admin.Profile.ADMIN_FETCH_TABLE_VIEW_LIST(), {params})
                    .then((response) => {
                        store.commit(Mutations.SET_VIEW_OPTION_LIST, {key: payload.key, viewOptionList: response.data});

                        // Checks if any view in list is default. If true, sets view as selected view
                        const defaultViewFromApi = response.data.find((view) => view.default);
                        if (defaultViewFromApi) {
                            store.commit(Mutations.SET_SELECTED_VIEW, {key: payload.key, view: defaultViewFromApi});
                        }
                    })
                    .catch((error) => {
                        if (error.response) {
                            const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                            store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                        } else {
                            console.log(error);
                        }
                    });
            }
        }
    }

    get getUnitOrderLinesTableConfig(): TableViewConfig {
        return {
            visibleColumns: [
               'orderId',
               'date',
               'type',
               'amount',
               'balance',
           ]
       };
    }

    get getUnitTransactionsTableConfig(): TableViewConfig {
        return {
            visibleColumns: [
               'orderId',
               'orderDate',
               'orderAmount',
               'currency',
               'unitTitle',
               'orderType',
               'orderDescription',
               'paymentStatus'
           ]
       };
    }

    get getCorporationUnpaidPaymentsTableConfig(): TableViewConfig {
        return {
            visibleColumns: [
                'id',
                'project',
                'unit',
                'description',
                'type',
                'amount',
                'vatAmount',
                'totalAmount'
            ]
        };
    }

    get getCorporationTransactionsTableConfig(): TableViewConfig {
        return {
            visibleColumns: [
                'id',
                'orderId',
                'orderDate',
                'totalAmount',
                'currency',
                'unitTitle',
                'project',
                'paymentStatus'
            ]
        };
    }
}
